"use client";

import { Checkbox, Input, Label, Textarea } from "@relume_io/relume-ui";
import { useState } from "react";
import { MonicateringButton } from "@/components/ui/MonicateringButton";
import { isValidEmail, isValidPhone } from "@/lib/contact-form";
import { trackContactFormConversion } from "@/lib/track-contact-conversion";

type ContactFormSectionClientProps = {
  buttonTitle: string;
};

export function ContactFormSectionClient({ buttonTitle }: ContactFormSectionClientProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [acceptTerms, setAcceptTerms] = useState<boolean | "indeterminate">(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [website, setWebsite] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const trimmedMessage = message.trim();
    const isTermsAccepted = acceptTerms === true;

    if (!trimmedName || !trimmedPhone) {
      setStatus({ type: "error", text: "Uzupełnij wszystkie wymagane pola." });
      return;
    }
    if (!isValidPhone(trimmedPhone)) {
      setStatus({ type: "error", text: "Podaj poprawny numer telefonu." });
      return;
    }
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setStatus({ type: "error", text: "Podaj poprawny adres e-mail." });
      return;
    }
    if (!isTermsAccepted) {
      setStatus({ type: "error", text: "Zaakceptuj politykę prywatności." });
      return;
    }

    setStatus(null);
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          phone: trimmedPhone,
          message: trimmedMessage,
          acceptTerms: isTermsAccepted,
          website,
        }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        throw new Error(data?.error || "Nie udało się wysłać formularza.");
      }

      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
      setAcceptTerms(false);
      setWebsite("");
      setStatus({ type: "success", text: "Dziękujemy! Wiadomość została wysłana." });
      trackContactFormConversion();
    } catch (error) {
      const text = error instanceof Error ? error.message : "Błąd wysyłki formularza.";
      setStatus({ type: "error", text });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="grid grid-cols-1 gap-6 text-on-secondary" onSubmit={handleSubmit}>
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />
      <div className="grid w-full items-center">
        <Label htmlFor="contact-name" className="sr-only">
          Imię i nazwisko
        </Label>
        <Input
          type="text"
          id="contact-name"
          required
          placeholder="Imię i nazwisko"
          className="!bg-white !text-black placeholder:!text-black/60"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="grid w-full items-center">
        <Label htmlFor="contact-email" className="sr-only">
          Adres e-mail
        </Label>
        <Input
          type="email"
          id="contact-email"
          placeholder="Email"
          className="!bg-white !text-black placeholder:!text-black/60"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="grid w-full items-center">
        <Label htmlFor="contact-phone" className="sr-only">
          Numer telefonu
        </Label>
        <Input
          type="tel"
          id="contact-phone"
          required
          placeholder="Numer telefonu *"
          className="!bg-white !text-black placeholder:!text-black/60"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>
      <div className="grid w-full items-center">
        <Label htmlFor="contact-message" className="sr-only">
          Treść wiadomości
        </Label>
        <Textarea
          id="contact-message"
          placeholder="Wiadomość"
          className="min-h-[11.25rem] overflow-auto !bg-white !text-black placeholder:!text-black/60"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      <div className="mb-3 flex items-center justify-between gap-4 text-sm text-on-primary md:mb-4">
        <div className="flex items-center space-x-2 [&_button]:border-on-primary [&_button]:bg-transparent [&_button]:text-on-primary [&_svg]:text-on-primary">
          <Checkbox
            id="contact-terms"
            required
            checked={acceptTerms}
            onCheckedChange={setAcceptTerms}
          />
          <Label htmlFor="contact-terms" className="cursor-pointer text-on-primary">
            Akceptuję{" "}
            <a className="text-on-primary underline decoration-on-primary/80 underline-offset-2 hover:opacity-90" href="/polityka-prywatnosci">
              politykę prywatności
            </a>
          </Label>
        </div>
        <MonicateringButton as="button" type="submit" variant="primary" disabled={isSubmitting}>
          {buttonTitle}
        </MonicateringButton>
      </div>
      {status ? (
        <p
          role={status.type === "error" ? "alert" : "status"}
          className={status.type === "error" ? "text-red-200" : "text-green-200"}
        >
          {status.text}
        </p>
      ) : null}
    </form>
  );
}
